/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

/**
 * The window a generated app opens at and the one it maximises to on a full-HD screen. Unless the user asks for
 * something else, the page fits both without scrolling (docs/plan/gui-layout.md); the build measures it at both.
 */
export const APP_WINDOW = {
  initial: { width: 1600, height: 900 },
  maximised: { width: 1920, height: 1080 },
  minimum: { width: 1280, height: 720 },
} as const;

/** The smallest text a page may have, in CSS pixels; a page is made to fit with its layout, not by shrinking text. */
export const MIN_FONT_PX = 12;

/** The arrangements a page is laid out in. Each fits 1600x900 and grows into 1920x1080. */
export const LAYOUT_PATTERNS = {
  'hero-inspector':
    'The main picture or live view takes two thirds to three quarters of the width; a panel of controls on the right, actions in a bar under the picture. For shooting and one camera.',
  'sidebar-main':
    'A list or navigation on the left (240-320 px wide); the chosen item in detail with its actions on the right. For several cameras, records, master-detail.',
  'panel-grid':
    'Panels in a grid under the header (two by two, or three columns), each scrolling inside. For monitoring and seeing several things at once.',
  'summary-detail':
    'A row of figure cards on top; tables or charts in two columns below. For checking results and records.',
  'steps-columns':
    'The steps of a task side by side as columns from left to right, all visible at once, the current one highlighted. For a procedure, also for beginners.',
  stepper:
    'A step indicator on top, one step at a time with Back and Next, and a panel on the right that sums up all steps. For setup and rare tasks.',
} as const;
export type LayoutPattern = keyof typeof LAYOUT_PATTERNS;
export const LayoutPatternSchema = z.enum(Object.keys(LAYOUT_PATTERNS) as [LayoutPattern, ...LayoutPattern[]]);

/** How the page is laid out: the planner decides it, the design drafts and the page follow it. */
export const LayoutPlanSchema = z.object({
  pattern: LayoutPatternSchema,
  fit: z
    .boolean()
    .default(true)
    .describe(
      'True (the default): the page fits a 1600x900 window and a maximised 1920x1080 one without scrolling the page. False only when the user asked for a layout that scrolls or a different screen (one column, portrait, phone-like).'
    ),
  regions: z
    .array(
      z.object({
        name: z.string().min(1).max(60).describe('Where on the screen, e.g. "left sidebar", "main", "right panel".'),
        parts: z.array(z.string().min(1).max(120)).max(12).describe('The parts of the brief shown there.'),
      })
    )
    .max(6)
    .default([]),
  tabs: z
    .array(z.string().min(1).max(60))
    .max(8)
    .default([])
    .describe('Screens or panels that share a place as tabs, because they need not be seen at once.'),
  scroll_inside: z
    .array(z.string().min(1).max(120))
    .max(8)
    .default([])
    .describe('Lists without a fixed length (history, log, records) that scroll inside their panel.'),
  note: z.string().max(300).optional().describe("The user's own words about the layout, when they gave any."),
});
export type LayoutPlan = z.infer<typeof LayoutPlanSchema>;

/** What a project keeps of its layout: the build holds a page that must fit to it (the "layout" gate). */
export const ProjectLayoutSchema = z.object({ pattern: z.string().max(40), fit: z.boolean() });
export type ProjectLayout = z.infer<typeof ProjectLayoutSchema>;

/** How a page fitted its window, as the app's probe measured it. */
export const PageLayoutSchema = z.object({
  viewport: z.object({ width: z.number(), height: z.number() }),
  page: z.object({ width: z.number(), height: z.number() }),
  page_screens: z.number(),
  overflow_x: z.boolean().optional(),
  clipped: z.array(z.string().max(200)).max(20).default([]),
  clipped_count: z.number().int().nonnegative().default(0),
  scrollers: z
    .array(z.object({ element: z.string().max(200), content: z.number() }))
    .max(20)
    .default([]),
  min_font_px: z.number().nullable().optional(),
  small_text: z.array(z.string().max(200)).max(20).default([]),
  /** The page draws a title bar, which a frameless window needs. */
  title_bar: z.boolean().optional(),
});
export type PageLayout = z.infer<typeof PageLayoutSchema>;
